import { useEffect, useRef } from 'react'

// Top-level navigation. Six tabs — collapsed from the original 11
// after the inventory + scheduling sub-screens were grouped under
// Stock and Schedule hubs respectively. The remaining tabs are the
// ones a manager mentally treats as separate destinations:
//
//   Huddle    — "what's happening today"
//   Stock     — anything to do with inventory (Receive / Reorder /
//                Stocktake / Variance / Waste live as sub-tabs)
//   Schedule  — staff schedule + catering bookings (sub-tabs)
//   Insights  — weekly numbers + AI brief + forecast + menu kill
//   Marketing — promotions + AI review reply
//   Settings  — items, recipes, suppliers, staff, compliance, account
//
// The horizontal-scroll behavior from the 11-tab era is preserved
// just in case we ever add a 7th, and because phones in landscape
// would benefit anyway.
export const TABS = [
  { id: 'huddle',    label: 'Huddle' },
  { id: 'stock',     label: 'Stock' },
  { id: 'schedule',  label: 'Schedule' },
  { id: 'insights',  label: 'Insights' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'settings',  label: 'Settings' },
]

export default function TabBar({ active, onChange }) {
  const tabRefs = useRef({})

  // When the active tab changes, smoothly scroll it into view so a
  // tab on the right side of the bar (e.g. Settings) becomes visible
  // when tapped via a metric card or quick action elsewhere.
  useEffect(() => {
    const tab = tabRefs.current[active]
    if (!tab) return
    try {
      tab.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',     // never scroll vertically
        inline: 'center',     // horizontally center the tab
      })
    } catch {
      // Older Safari without options support
      tab.scrollIntoView()
    }
  }, [active])

  return (
    <nav
      aria-label="Screens"
      className="
        mx-auto flex max-w-screen-sm overflow-x-auto
        [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
      "
    >
      {TABS.map((t) => {
        const isActive = active === t.id
        return (
          <button
            key={t.id}
            type="button"
            ref={(el) => { tabRefs.current[t.id] = el }}
            onClick={() => onChange(t.id)}
            aria-current={isActive ? 'page' : undefined}
            className={`
              min-h-btn shrink-0 whitespace-nowrap
              px-4 pt-2 pb-3
              text-base font-medium transition border-b-4
              ${isActive
                ? 'border-amber-brand-600 text-amber-brand-800'
                : 'border-transparent text-amber-brand-800 hover:text-amber-brand-800'}
            `}
          >
            {t.label}
          </button>
        )
      })}
    </nav>
  )
}
